import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { JournalId } from 'types';
import {
    MutationInvalidateQueryKeysType,
    useAppMutation,
} from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { commonTexts } from 'consts/text';
import { parseStringIntoDayjsDate } from 'utils/parsers/dateTime/parseStringIntoDayjsDate';
import { useFirstAndLastJournalSchoolYears } from 'hooks/useFirstAndLastJournalSchoolYears/useFirstAndLastJournalSchoolYears';
import { getMonthFromDayjsMonth } from 'utils/getMonthFromDayjsMonth';

type GroupJournalDailyAttendancePageNetworkManageProps = {
    journalId: JournalId;
    dateFilterValue: string;
};

export const useGroupJournalDailyAttendancePageNetworkManage = ({
    journalId,
    dateFilterValue,
}: GroupJournalDailyAttendancePageNetworkManageProps) => {
    const { displayError } = useSimpleNotification();

    const selectedDayjsDate = parseStringIntoDayjsDate(dateFilterValue);
    const selectedYear = selectedDayjsDate.year();
    const selectedMonth = getMonthFromDayjsMonth(selectedDayjsDate.month());

    const dailyAttendanceActionInvalidateQueryKeys: MutationInvalidateQueryKeysType[] = [
        ['DAILY_ATTENDANCE', journalId, dateFilterValue],
        ['MONTHLY_ATTENDANCE', journalId, selectedYear, selectedMonth],
    ];

    const {
        data: dailyAttendanceData,
        isInitialLoading: isDailyAttendanceDataInitialLoading,
        isRefetching: isDailyAttendanceDataRefetching,
        error: dailyAttendanceDataError,
    } = useAppQuery(
        'DAILY_ATTENDANCE',
        [journalId, dateFilterValue],
        () => StaffHTTPService.groupAttendance.getGroupDailyAttendance(journalId, dateFilterValue),
        { retry: false },
    );

    const { firstAndLastJournalSchoolYears, isJournalDataFetching, isJournalDataError } =
        useFirstAndLastJournalSchoolYears({ journalId });

    const { mutate: updateDailyAttendance, isLoading: isUpdateDailyAttendanceLoading } =
        useAppMutation(StaffHTTPService.groupAttendance.updateGroupDailyAttendance, {
            key: ['UPDATE_DAILY_ATTENDANCE'],
            onError: () => displayError(commonTexts.errorMessages.actionExecution),
            invalidateQueryKeys: dailyAttendanceActionInvalidateQueryKeys,
        });

    const { mutate: deleteDailyAttendance, isLoading: isDeleteDailyAttendanceLoading } =
        useAppMutation(StaffHTTPService.groupAttendance.deleteGroupDailyAttendance, {
            key: ['DELETE_DAILY_ATTENDANCE'],
            onError: () => displayError(commonTexts.errorMessages.actionExecution),
            invalidateQueryKeys: dailyAttendanceActionInvalidateQueryKeys,
        });

    return {
        dailyAttendanceData,
        firstAndLastJournalSchoolYears,
        isJournalDataError,
        isDailyAttendanceDataInitialLoading,
        isJournalDataFetching,
        isUpdateDailyAttendanceLoading,
        isDeleteDailyAttendanceLoading,
        isDailyAttendanceDataRefetching,
        dailyAttendanceDataError,
        updateDailyAttendance,
        deleteDailyAttendance,
    };
};
